// Generative ambient audio engine for Jozori.
// No external assets — uses Web Audio API for seamless, low-battery atmospheres.
//
// Modes:
//   - "silent"  : nothing plays
//   - "ambient" : soft world-tinted drone + airy wash (default per world)
//   - "rain"    : pink-noise rain through bandpass + gentle drops
//   - "bedtime" : very low warm drone + slow breathing swell
//
// All transitions fade over ~1.6s. Master gain is conservative (children).

export type AmbientMode = "silent" | "ambient" | "rain" | "bedtime";
export type AmbientWorld = "garden" | "library" | "workshop" | "square" | null;

type Voice = {
  stop: (fadeMs?: number) => void;
};

const MASTER_DEFAULT = 0.25; // 25%
const FADE_MS = 1600;

// World-tinted base frequencies (low, warm, non-melodic).
const WORLD_FREQ: Record<NonNullable<AmbientWorld>, number[]> = {
  garden:   [110.0, 164.81, 220.0],  // A2, E3, A3 — open and bright
  library:  [87.31, 130.81, 174.61], // F2, C3, F3 — warm, contemplative
  workshop: [98.0,  146.83, 196.0],  // G2, D3, G3 — grounded
  square:   [82.41, 123.47, 164.81], // E2, B2, E3 — soft minor
};

class AmbientEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private active: Voice[] = [];
  private mode: AmbientMode = "silent";
  private world: AmbientWorld = null;
  private volume = MASTER_DEFAULT;
  private muted = false;
  private visibilityBound = false;

  private ensureCtx(): AudioContext | null {
    if (typeof window === "undefined") return null;
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return null;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : this.volume;
      this.master.connect(this.ctx.destination);
      this.bindLifecycle();
    }
    return this.ctx;
  }

  private bindLifecycle() {
    if (this.visibilityBound || typeof document === "undefined") return;
    this.visibilityBound = true;
    document.addEventListener("visibilitychange", () => {
      if (!this.ctx) return;
      if (document.hidden) {
        // pause to save battery and handle backgrounding/calls
        this.ctx.suspend().catch(() => {});
      } else if (this.mode !== "silent") {
        this.ctx.resume().catch(() => {});
      }
    });
  }

  // ---- voice builders ----------------------------------------------------

  private noiseBuffer(seconds = 4): AudioBuffer | null {
    const ctx = this.ctx!;
    const buf = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
    const data = buf.getChannelData(0);
    // pink-ish noise (Voss-McCartney lite)
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + white * 0.0990460;
      b1 = 0.96300 * b1 + white * 0.2965164;
      b2 = 0.57000 * b2 + white * 1.0526913;
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.18;
    }
    return buf;
  }

  private startDrone(freq: number, gainLevel: number, detune = 0): Voice {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const g = ctx.createGain();
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 800;
    osc.type = "sine";
    osc2.type = "triangle";
    osc.frequency.value = freq;
    osc2.frequency.value = freq * 2.003;
    osc2.detune.value = detune;
    g.gain.value = 0;
    osc.connect(g);
    osc2.connect(g);
    g.connect(lp).connect(this.master!);
    const now = ctx.currentTime;
    g.gain.linearRampToValueAtTime(gainLevel, now + FADE_MS / 1000);
    osc.start();
    osc2.start();
    return {
      stop: (fadeMs = FADE_MS) => {
        const t = ctx.currentTime;
        g.gain.cancelScheduledValues(t);
        g.gain.setValueAtTime(g.gain.value, t);
        g.gain.linearRampToValueAtTime(0.0001, t + fadeMs / 1000);
        osc.stop(t + fadeMs / 1000 + 0.05);
        osc2.stop(t + fadeMs / 1000 + 0.05);
      },
    };
  }

  private startNoise(opts: {
    bandHz?: number;
    q?: number;
    gain: number;
    type?: BiquadFilterType;
  }): Voice {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(6);
    src.loop = true;
    const filt = ctx.createBiquadFilter();
    filt.type = opts.type ?? "lowpass";
    if (opts.bandHz) filt.frequency.value = opts.bandHz;
    if (opts.q) filt.Q.value = opts.q;
    const g = ctx.createGain();
    g.gain.value = 0;
    src.connect(filt).connect(g).connect(this.master!);
    const now = ctx.currentTime;
    g.gain.linearRampToValueAtTime(opts.gain, now + FADE_MS / 1000);
    src.start();
    return {
      stop: (fadeMs = FADE_MS) => {
        const t = ctx.currentTime;
        g.gain.cancelScheduledValues(t);
        g.gain.setValueAtTime(g.gain.value, t);
        g.gain.linearRampToValueAtTime(0.0001, t + fadeMs / 1000);
        src.stop(t + fadeMs / 1000 + 0.05);
      },
    };
  }

  private startBreath(): Voice {
    // slow LFO modulating a low pad — bedtime "breathing"
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 65.41; // C2
    lfo.frequency.value = 0.09;  // ~5.5s cycle
    lfoGain.gain.value = 0.04;
    g.gain.value = 0;
    lfo.connect(lfoGain).connect(g.gain);
    osc.connect(g).connect(this.master!);
    const now = ctx.currentTime;
    g.gain.linearRampToValueAtTime(0.05, now + FADE_MS / 1000);
    osc.start();
    lfo.start();
    return {
      stop: (fadeMs = FADE_MS) => {
        const t = ctx.currentTime;
        g.gain.cancelScheduledValues(t);
        g.gain.setValueAtTime(g.gain.value, t);
        g.gain.linearRampToValueAtTime(0.0001, t + fadeMs / 1000);
        osc.stop(t + fadeMs / 1000 + 0.05);
        lfo.stop(t + fadeMs / 1000 + 0.05);
      },
    };
  }

  // ---- public API --------------------------------------------------------

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master && !this.muted) {
      const t = this.ctx!.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.linearRampToValueAtTime(this.volume, t + 0.4);
    }
  }

  setMuted(m: boolean) {
    this.muted = m;
    if (this.master && this.ctx) {
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.linearRampToValueAtTime(m ? 0 : this.volume, t + 0.4);
    }
  }

  getState() {
    return { mode: this.mode, world: this.world, volume: this.volume, muted: this.muted };
  }

  setWorld(world: AmbientWorld) {
    if (world === this.world) return;
    this.world = world;
    if (this.mode === "ambient") this.apply(); // re-tint
  }

  setMode(mode: AmbientMode) {
    if (mode === this.mode) return;
    this.mode = mode;
    this.apply();
  }

  private stopAll(fadeMs = FADE_MS) {
    for (const v of this.active) v.stop(fadeMs);
    this.active = [];
  }

  private apply() {
    const ctx = this.ensureCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    // crossfade
    this.stopAll(FADE_MS);

    if (this.mode === "silent") return;

    if (this.mode === "ambient") {
      const freqs = (this.world && WORLD_FREQ[this.world]) || WORLD_FREQ.garden;
      this.active.push(this.startDrone(freqs[0], 0.06));
      this.active.push(this.startDrone(freqs[1], 0.035, 6));
      this.active.push(this.startNoise({ bandHz: 1800, gain: 0.025, type: "lowpass" }));
    } else if (this.mode === "rain") {
      this.active.push(this.startNoise({ bandHz: 1200, q: 0.4, gain: 0.18, type: "lowpass" }));
      this.active.push(this.startNoise({ bandHz: 3500, q: 0.7, gain: 0.06, type: "bandpass" }));
      this.active.push(this.startDrone(98, 0.025));
    } else if (this.mode === "bedtime") {
      this.active.push(this.startBreath());
      this.active.push(this.startDrone(98, 0.03));
      this.active.push(this.startNoise({ bandHz: 600, gain: 0.012, type: "lowpass" }));
    }
  }

  // call once after first user gesture (autoplay policy)
  unlock() {
    const ctx = this.ensureCtx();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  }
}

let engine: AmbientEngine | null = null;
export function getAmbientEngine(): AmbientEngine {
  if (!engine) engine = new AmbientEngine();
  return engine;
}
